import api from "../api/config";
import { ENDPOINTS } from "../api/endpoints";
import { normalizeCompanyName } from "../utils/companyNameMapper";
import { sanitizeString, sanitizeStatusArray } from '../helpers/zohoSanitizer.js';

/**
 * Fetch Zoho leads with pagination and filtering options
 * @param {Object} params - The parameters for fetching leads
 * @param {string} params.company - Company name
 * @param {number} params.page - Page number for pagination
 * @param {string} [params.agent] - Filter by agent name
 * @param {string} [params.searchValue] - Search value
 * @param {string} [params.searchType] - Type of search
 * @returns {Promise<Object>} - Returns a promise that resolves to the leads data
 */
export const getZohoLeads = async (params) => {
  if (!params.company) {
    return null;
  }

  try {
    return await api.post(ENDPOINTS.GET_ZOHO_LEADS, params);
  } catch (error) {
    console.error('Failed to fetch Zoho leads', error);
    return null;
  }
};

/**
 * @typedef {Object} LeadInfo
 * @property {string} id - Lead ID
 * @property {string} [status] - Lead status
 * @property {string} [company] - Company name
 */

/**
 * Get lead information by merchant ID
 * @param {string} mid - Merchant ID to fetch information for
 * @returns {Promise<LeadInfo|null>} Lead information or null if not found
 */
export const getLeadInfo = async (mid) => {
  if (!mid) {
    return null;
  }

  try {
    const { data } = await api.post(ENDPOINTS.GET_LEAD_INFO, { mid });
    if (!data?.data?.[0]) {
      return null;
    }
    return data.data[0];
  } catch (error) {
    console.error('Failed to fetch lead information for MID:', mid, error);
    return null;
  }
};

/**
 * Update Zoho lead information
 * @returns {Promise<boolean>} Returns true if the update is successful, false otherwise
 */
export const updateZohoLead = async () => {
  try {
    await api.post(ENDPOINTS.UPDATE_ZOHO_LEAD, {});
    return true;
  } catch (error) {
    console.error('Failed to update lead', error);
    return false;
  }
};

/**
 * Combined search function that supports multiple filters simultaneously
 * Supports business name, gateway ID, date ranges, and status filters together
 * 
 * @param {Object} params - Search parameters
 * @param {string} params.company - Company name
 * @param {string} [params.businessName] - Business name search term
 * @param {string} [params.gatewayId] - Gateway ID search term
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD) for custom range
 * @param {string} [params.endDate] - End date (YYYY-MM-DD) for custom range
 * @param {string} [params.rangeType] - Predefined range type (today, yesterday, etc.)
 * @param {string} [params.dateField] - Date field to filter on (submitted, approval, lastUpdated)
 * @param {Array<string>} [params.status] - Array of status values
 * @param {string} [params.salesRep] - Sales rep name
 * @param {number} [params.page=1] - Page number for pagination
 * @param {number} [params.perPage=50] - Records per page
 * @returns {Promise<Object|null>} - Returns a promise that resolves to the search results
 */
export const searchLeadsCombined = async (params) => {
  if (!params.company) {
    return null;
  }

  // Normalize company name using centralized mapper
  const cleanParams = { ...params, company: normalizeCompanyName(params.company) };

  if (cleanParams.businessName) cleanParams.businessName = sanitizeString(cleanParams.businessName);
  if (cleanParams.salesRep) cleanParams.salesRep = sanitizeString(cleanParams.salesRep);
  if (cleanParams.gatewayId) cleanParams.gatewayId = sanitizeString(cleanParams.gatewayId);

  if (cleanParams.status && cleanParams.status.length > 0) {
    cleanParams.status = sanitizeStatusArray(cleanParams.status);
  }

  try {
    const { data } = await api.post(ENDPOINTS.SEARCH_COMBINED, cleanParams);
    return data;
  } catch (error) {
    console.error('Combined search error:', error);
    return null;
  }
};

/**
 * Export leads with filters to CSV
 * @param {string} company - Company name
 * @param {Object} filters - Filter options
 * @param {string} [filters.startDate] - Start date for date range filter
 * @param {string} [filters.endDate] - End date for date range filter
 * @param {Array} [filters.status] - Array of status filters
 * @param {string} [filters.salesRep] - Sales representative filter
 * @returns {Promise<void>} - Downloads CSV file
 */
export const exportLeadsWithFilters = async (company, filters) => {
  if (!company) {
    return { success: false, message: 'Company is required' };
  }

  try {
    const response = await api.post(ENDPOINTS.EXPORT_LEADS, {
      company,
      filters
    }, {
      responseType: 'blob' // Handle CSV response as blob
    });

    // Create and download the CSV file
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create temporary link element for download
    const link = document.createElement('a');
    link.href = url;
    link.download = `merchants_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.style.visibility = 'hidden';

    // Trigger download and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);

    return { success: true, message: 'CSV export completed successfully' };
  } catch (error) {
    console.error('Export error:', error);
    if (error.status === 404) {
      return { success: false, message: error.data?.message || 'No records found for the given filters' };
    }
    return { success: false, message: 'Failed to export leads' };
  }
};

/**
 * Update notes for a lead in Zoho CRM with timestamp handling
 * @param {Object} params - The parameters for updating notes
 * @param {string} params.zohoID - Zoho lead ID
 * @param {string} params.notes - New note content
 * @param {boolean} params.addTimestamp - Whether to add timestamp to new and existing notes
 * @returns {Promise<Object>} - Returns a promise that resolves to the update response
 */
export const updateNotes = async (params) => {
  if (!params.zohoID || !params.notes) {
    return { success: false, error: 'Zoho ID and notes are required' };
  }

  try {
    const { data } = await api.put(ENDPOINTS.UPDATE_LEAD_NOTES, {
      zohoID: params.zohoID,
      notes: params.notes,
      addTimestamp: params.addTimestamp || false
    });
    return data;
  } catch (error) {
    console.error('Notes update error:', error);
    return { success: false, error: error.response?.data?.message || 'Failed to update notes' };
  }
};

export default {
  getZohoLeads,
  getLeadInfo,
  updateZohoLead,
  updateNotes,
  searchLeadsCombined,
  exportLeadsWithFilters
};